import { useCallback, useEffect, useRef, useState } from 'react';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useToast } from '@/hooks/use-toast';
import { apiClient } from '@/lib/api/axios';

import type { ReactNode } from 'react';

export type AudioSourceKind = 'provider' | 'internal';

export interface AudioSourceInfo {
  kind: AudioSourceKind;
  url: string;
}

interface RecordingPlayerProps {
  callId: string;
  recordingPath: string | null | undefined;
  internalRecordingPath: string | null | undefined;
  /** Expose the audio element ref so the parent can seek */
  onAudioRef?: (el: HTMLAudioElement | null) => void;
  /** Fires whenever the loaded source changes (e.g. for download buttons) */
  onSourceChange?: (info: AudioSourceInfo | null) => void;
  /** Extra content rendered inside the card below the player (e.g. download buttons) */
  children?: ReactNode;
}

export const RecordingPlayer = ({
  callId,
  recordingPath,
  internalRecordingPath,
  onAudioRef,
  onSourceChange,
  children,
}: RecordingPlayerProps) => {
  const { toast } = useToast();
  const [audioSourceKind, setAudioSourceKind] = useState<AudioSourceKind | null>(null);
  const [audioPlayerUrl, setAudioPlayerUrl] = useState<string | null>(null);
  const [audioLoading, setAudioLoading] = useState(false);
  const [audioLoadError, setAudioLoadError] = useState<string | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  // Kept in a ref so an unmemoised callback from a consumer can never re-trigger the load effect.
  const onSourceChangeRef = useRef(onSourceChange);
  useEffect(() => {
    onSourceChangeRef.current = onSourceChange;
  });

  const setRef = useCallback(
    (el: HTMLAudioElement | null) => {
      audioRef.current = el;
      onAudioRef?.(el);
    },
    [onAudioRef],
  );

  const loadAudioSource = useCallback(
    async (source: AudioSourceKind, options?: { silent?: boolean }) => {
      const endpoint =
        source === 'provider'
          ? `/storage/recordings/${callId}`
          : `/storage/recordings/${callId}/internal`;

      try {
        setAudioLoading(true);
        setAudioLoadError(null);
        const response = await apiClient.get<{ url: string }>(endpoint);
        setAudioPlayerUrl(response.data.url);
        setAudioSourceKind(source);
        onSourceChangeRef.current?.({ kind: source, url: response.data.url });
      } catch {
        const message =
          source === 'provider'
            ? 'Failed to load provider recording for playback.'
            : 'Failed to load internal recording for playback.';
        setAudioLoadError(message);
        if (!options?.silent) {
          toast({
            variant: 'destructive',
            title: 'Audio Load Failed',
            description: message,
          });
        }
      } finally {
        setAudioLoading(false);
      }
    },
    [callId, toast],
  );

  useEffect(() => {
    setAudioPlayerUrl(null);
    setAudioSourceKind(null);
    onSourceChangeRef.current?.(null);

    const preferredSource: AudioSourceKind | null = recordingPath
      ? 'provider'
      : internalRecordingPath
        ? 'internal'
        : null;

    if (preferredSource) {
      void loadAudioSource(preferredSource, { silent: true });
    }
  }, [callId, recordingPath, internalRecordingPath, loadAudioSource]);

  if (!recordingPath && !internalRecordingPath) return null;

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm">Recording</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <audio
            ref={setRef}
            controls
            preload="metadata"
            src={audioPlayerUrl || undefined}
            className="w-full"
          >
            Your browser does not support audio playback.
          </audio>

          <div className="mt-3 flex flex-wrap gap-2">
            {recordingPath && (
              <Button
                type="button"
                size="sm"
                variant={audioSourceKind === 'provider' ? 'default' : 'outline'}
                onClick={() => void loadAudioSource('provider')}
                disabled={audioLoading}
              >
                Use Provider Audio
              </Button>
            )}
            {internalRecordingPath && (
              <Button
                type="button"
                size="sm"
                variant={audioSourceKind === 'internal' ? 'default' : 'outline'}
                onClick={() => void loadAudioSource('internal')}
                disabled={audioLoading}
              >
                Use Internal Audio
              </Button>
            )}
          </div>

          {audioSourceKind && (
            <p className="text-muted-foreground mt-2 text-xs">
              Playing {audioSourceKind === 'provider' ? 'provider' : 'internal'} recording.
            </p>
          )}
          {audioLoading && <p className="text-muted-foreground mt-2 text-xs">Loading audio...</p>}
          {audioLoadError && <p className="mt-2 text-xs text-red-600">{audioLoadError}</p>}
        </div>

        {children}
      </CardContent>
    </Card>
  );
};
